import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import type {AnimationTrimThumbnailStripProps} from './animation-trim-thumbnail-strip.types';
import styles from './animation-trim-thumbnail-strip.module.scss';
import {ANIMATION_THUMBNAIL_STRIP_HEIGHT, drawCanvasOntoCanvas, generateThumbnailStripCanvas} from '@Utils/video.util';
import {v4 as uuidv4} from 'uuid';

const FALLBACK_WIDTH = 400;

export function AnimationTrimThumbnailStrip({source, duration, videoWidth, videoHeight, className, preRenderedCanvas, onReady}: AnimationTrimThumbnailStripProps): ReactElement {
  const canvasIdRef = useRef(uuidv4());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(!preRenderedCanvas);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.height = `${ANIMATION_THUMBNAIL_STRIP_HEIGHT}px`;
    }

    if (preRenderedCanvas && canvasRef.current) {
      drawCanvasOntoCanvas(preRenderedCanvas, canvasRef.current);
      setIsLoading(false);
      onReady?.();
      return;
    }

    setIsLoading(true);
    const maxContentWidth = containerRef.current?.offsetWidth ?? FALLBACK_WIDTH;
    generateThumbnailStripCanvas(source, duration, videoWidth, videoHeight, maxContentWidth)
      .then((canvas) => {
        if (canvasRef.current) {
          drawCanvasOntoCanvas(canvas, canvasRef.current);
        }
        setIsLoading(false);
        onReady?.();
      })
      .catch((e: unknown) => {
        console.error(e);
        setIsLoading(false);
        onReady?.();
      });
  }, [preRenderedCanvas]);

  return (
    <div ref={containerRef} className={`${styles.container} ${className ?? ''}`}>
      {isLoading ? (
        <SkeletonBrick className={styles.loadingPlaceholder} theme={SkeletonBrickTheme.DARK} isPulsating width="100%" height="100%" />
      ) : (
        <canvas id={canvasIdRef.current} ref={canvasRef} className={styles.canvas} />
      )}
    </div>
  );
}
